import {
  Auth0AccountRole,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  MeetingStudentAdminResponse,
  MeetingStudentLimitedResponse,
  MeetingTutorAdminResponse,
  MeetingTutorLimitedResponse,
  OrganizationSubjectAdminResponse,
  OrganizationSubjectPublicResponse,
  OrgSubjectsService,
  StudentAdminResponse,
  StudentLimitedResponse,
  TutorAdminResponse,
  TutorLimitedResponse,
} from "client/openapi";
import { GridIcon } from "@radix-ui/react-icons";
import { TutorContext } from "components/Dashboard/TutorDashboard/tutorContext";
import { AttendeesList } from "components/MeetingAttendees";
import type { MeetingDetails } from "components/MeetingDialog";
import { Select } from "components/Select";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import "./meetingFields.css";

type Subject =
  | OrganizationSubjectPublicResponse
  | OrganizationSubjectAdminResponse;
type Tutor =
  | MeetingTutorLimitedResponse
  | MeetingTutorAdminResponse
  | TutorLimitedResponse
  | TutorAdminResponse;
type Student =
  | MeetingStudentLimitedResponse
  | MeetingStudentAdminResponse
  | StudentLimitedResponse
  | StudentAdminResponse;

const uniqSubjects = (items: Subject[]) =>
  Array.from(new Map(items.map((s) => [s.id, s])).values());

/** A field's label row: one line, the same height for every field. */
export const FIELD_LABEL =
  "flex h-6 items-center gap-2 mb-1 whitespace-nowrap text-xs font-bold";

/**
 * A small grid icon beside a field's label that shows that field's grid
 * (tutor availability or rooms); green while that grid is shown.
 */
export function GridViewButton({
  label,
  active = false,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          className={`ml-auto rounded p-0.5 ${
            active
              ? "bg-green-100 text-green-700"
              : "text-gray-500 hover:bg-gray-200 hover:text-green-700"
          }`}
          onClick={onClick}
        >
          <GridIcon />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The meeting's tutors and students (set once for all its sessions): two
 * cells of Book Meeting's field grid, each a search select holding the
 * chosen people as chips.
 */
export function AttendeeFields({
  tutors,
  students,
  event,
  creating,
  subject,
  currentMeetingInfo,
  orgId,
  removeAttendee,
  updateAttendees,
  onSubjectChange,
  onShowTutorAvailability,
  tutorAvailabilityShown = false,
}: {
  tutors: Tutor[];
  students: Student[];
  event?: MeetingAdminResponse | MeetingLimitedResponse;
  creating: boolean;
  subject?: Subject;
  currentMeetingInfo: MeetingDetails;
  orgId: number;
  removeAttendee: (userType: Auth0AccountRole, id: number) => void;
  updateAttendees: (
    userType: Auth0AccountRole,
    attendees: (Tutor | Student)[]
  ) => void;
  onSubjectChange: (subject: Subject | undefined) => void;
  /** Shows the tutor availability icon beside Tutors when provided. */
  onShowTutorAvailability?: () => void;
  tutorAvailabilityShown?: boolean;
}) {
  // When the organization changes, the subject and people belong to the old one.
  const prevOrgIdRef = useRef<number>(orgId);
  useEffect(() => {
    if (prevOrgIdRef.current !== orgId) {
      onSubjectChange(undefined);
      updateAttendees(Auth0AccountRole.ORG_TUTOR, []);
      updateAttendees(Auth0AccountRole.ME, []);
    }
    prevOrgIdRef.current = orgId;
  }, [orgId, onSubjectChange, updateAttendees]);

  const shared = {
    subject,
    editing: true,
    creating,
    event,
    currentMeetingInfo,
    orgId,
    remove: removeAttendee,
    updateAttendees,
  };

  return (
    <>
      <div className="min-w-0">
        <div className={FIELD_LABEL}>
          Tutors
          {onShowTutorAvailability && (
            <GridViewButton
              label="Tutor availability"
              active={tutorAvailabilityShown}
              onClick={onShowTutorAvailability}
            />
          )}
        </div>
        <AttendeesList
          {...shared}
          attendees={tutors}
          typeOfAttendees={Auth0AccountRole.ORG_TUTOR}
          linkPrefix="/manage/tutors/"
        />
      </div>
      <div className="min-w-0">
        <div className={FIELD_LABEL}>Students</div>
        <AttendeesList
          {...shared}
          attendees={students}
          typeOfAttendees={Auth0AccountRole.ME}
          linkPrefix="/manage/students/"
        />
      </div>
    </>
  );
}

/**
 * The meeting's subject. Admins see the chosen people's subjects first; a
 * tutor sees only their own subjects.
 */
export function SubjectField({
  availableSubjects,
  subject,
  currentMeetingInfo,
  onChange,
}: {
  availableSubjects: Subject[] | undefined;
  subject?: Subject;
  currentMeetingInfo: MeetingDetails;
  onChange: (subject: Subject | undefined) => void;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const { current_tutor } = useContext(TutorContext);

  const [suggestedSubjects, setSuggestedSubjects] = useState<Subject[]>([]);
  const [tutorSubjectsOnly, setTutorSubjectsOnly] = useState<Subject[]>([]);
  const [subjectInput, setSubjectInput] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function buildForAdmin() {
      try {
        const subjByStudents = await Promise.all(
          (currentMeetingInfo.student_ids || []).map((sid: number) =>
            OrgSubjectsService.getOrgSubjectsByStudent({
              studentId: sid,
              activeForOrg: true,
              activeForStudent: true,
            }).catch(() => [])
          )
        );

        const subjByTutors = await Promise.all(
          (currentMeetingInfo.tutor_ids || []).map((tid: number) =>
            OrgSubjectsService.getOrgSubjectsByTutor({
              tutorId: tid,
              activeForOrg: true,
              activeForTutor: true,
            }).catch(() => [])
          )
        );

        const merged = uniqSubjects([
          ...subjByStudents.flat(),
          ...subjByTutors.flat(),
        ]);

        if (!cancelled) setSuggestedSubjects(merged);
      } catch {
        if (!cancelled) setSuggestedSubjects([]);
      }
    }

    async function buildForTutor() {
      if (!current_tutor) {
        setTutorSubjectsOnly([]);
        return;
      }
      const subs = await OrgSubjectsService.getOrgSubjectsByTutor({
        tutorId: current_tutor.id,
        activeForOrg: true,
        activeForTutor: true,
      }).catch(() => []);
      if (!cancelled) setTutorSubjectsOnly(subs);
    }

    if (currently_selected_role === Auth0AccountRole.ORG_ADMIN) {
      buildForAdmin();
    } else if (currently_selected_role === Auth0AccountRole.ORG_TUTOR) {
      buildForTutor();
    } else {
      setSuggestedSubjects([]);
      setTutorSubjectsOnly([]);
    }

    return () => {
      cancelled = true;
    };
  }, [
    current_tutor,
    currently_selected_role,
    currentMeetingInfo.student_ids,
    currentMeetingInfo.tutor_ids,
  ]);

  const subjectOptions = useMemo(() => {
    const all = (availableSubjects || []) as Subject[];

    const filter = (arr: Subject[]) =>
      arr.filter((s) =>
        s.name.toLowerCase().includes(subjectInput.toLowerCase())
      );

    if (currently_selected_role === Auth0AccountRole.ORG_TUTOR) {
      return subjectInput ? filter(tutorSubjectsOnly) : tutorSubjectsOnly;
    }

    // Admin:
    if (subjectInput) {
      return filter(all);
    }

    // No input: Suggested first, then remaining
    const suggestedIds = new Set(suggestedSubjects.map((s) => s.id));
    const remaining = all.filter((s) => !suggestedIds.has(s.id));
    return suggestedSubjects.length
      ? [
          { label: "Suggested subjects", options: suggestedSubjects },
          { label: "All subjects", options: remaining },
        ]
      : all;
  }, [
    availableSubjects,
    suggestedSubjects,
    tutorSubjectsOnly,
    subjectInput,
    currently_selected_role,
  ]);

  return (
    <div className="min-w-0">
      <div className={FIELD_LABEL}>Subject</div>
      <Select
        id="subject"
        // Sized with Book Meeting's other fields (meetingFields.css).
        classNamePrefix="meeting-field"
        options={subjectOptions as any}
        value={subject ?? null}
        getOptionLabel={(s: any) => `${s.name}`}
        getOptionValue={(s: any) => s.id.toString()}
        placeholder="Subject…"
        onChange={onChange}
        onInputChange={(val: string) => setSubjectInput(val)}
        noOptionsMessage={() =>
          subjectInput ? "No matching subjects" : "Select a subject"
        }
        title="Add subject"
      />
    </div>
  );
}
